"use client"

import * as React from "react"
import { use } from "react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { 
  ArrowLeft, 
  Calendar, 
  MapPin, 
  Clock, 
  DollarSign, 
  Users,
  CreditCard,
  Check,
  Shield,
  AlertTriangle
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Header } from "@/components/header"
import { Footer } from "@/components/footer"
import { LEAGUES } from "@/lib/leagues-data"

interface SignupPageProps {
  params: Promise<{ id: string }>
}

export default function SignupPage({ params }: SignupPageProps) {
  const { id } = use(params)
  const router = useRouter()
  const league = LEAGUES.find(l => l.id === id)

  const [step, setStep] = React.useState<"review" | "payment" | "confirm">("review")
  const [isLoading, setIsLoading] = React.useState(false)
  const [agreedToRules, setAgreedToRules] = React.useState(false)
  const [preferredRole, setPreferredRole] = React.useState("")

  // For demo purposes, assume user is logged in
  const isLoggedIn = true
  const user = { displayName: "Player", email: "player@example.com" }

  if (!league) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 pt-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground mb-2">League not found</h1>
            <p className="text-muted-foreground mb-4">This league doesn&apos;t exist or has been removed.</p>
            <Button asChild>
              <Link href="/leagues">Browse Leagues</Link>
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    )
  }

  // Redirect to register if not logged in
  if (!isLoggedIn) {
    router.push(`/register?redirect=/leagues/${league.id}/signup`)
    return null
  }

  const handlePayment = async () => {
    setIsLoading(true)
    // Simulate payment processing
    await new Promise(resolve => setTimeout(resolve, 1500))
    setStep("confirm")
    setIsLoading(false)
  }

  if (step === "confirm") {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 pt-20 flex items-center justify-center px-6 py-12">
          <Card className="max-w-md w-full border-primary/30">
            <CardContent className="p-8 text-center">
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                <Check className="h-8 w-8 text-primary" />
              </div>
              <h1 className="text-2xl font-bold text-foreground mb-2">You&apos;re In!</h1>
              <p className="text-muted-foreground mb-6">
                You&apos;ve successfully registered for {league.name}. We&apos;ll send you an email with next steps.
              </p>
              
              <div className="bg-secondary/50 rounded-lg p-4 text-left mb-6">
                <h3 className="font-semibold text-foreground mb-3">What happens next:</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold">1.</span>
                    Join the league Discord server (link in email)
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold">2.</span>
                    Team assignments sent 24h before start
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold">3.</span>
                    First match: {league.day} @ {league.time} {league.timezone}
                  </li>
                </ul>
              </div>

              <div className="space-y-3">
                <Button asChild className="w-full bg-primary hover:bg-primary/90">
                  <Link href="/my-leagues">View My Leagues</Link>
                </Button>
                <Button asChild variant="outline" className="w-full border-border">
                  <Link href="/leagues">Browse More Leagues</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <main className="flex-1 pt-20">
        <div className="mx-auto max-w-4xl px-6 lg:px-8 py-8">
          <Link 
            href={`/leagues/${league.id}`}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to League Details
          </Link>

          <div className="grid lg:grid-cols-5 gap-8">
            {/* Main Form */}
            <div className="lg:col-span-3">
              <h1 className="text-2xl font-bold text-foreground mb-2">
                Sign Up for {league.name}
              </h1>
              <p className="text-muted-foreground mb-8">
                Complete your registration to secure your spot.
              </p>

              {/* Progress Steps */}
              <div className="flex items-center gap-2 mb-8">
                <div className={`flex items-center gap-2 ${step === "review" ? "text-primary" : "text-muted-foreground"}`}>
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step === "review" ? "bg-primary text-primary-foreground" : "bg-secondary"
                  }`}>
                    1
                  </div>
                  <span className="text-sm font-medium">Review</span>
                </div>
                <div className="flex-1 h-px bg-border" />
                <div className={`flex items-center gap-2 ${step === "payment" ? "text-primary" : "text-muted-foreground"}`}>
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step === "payment" ? "bg-primary text-primary-foreground" : "bg-secondary"
                  }`}>
                    2
                  </div>
                  <span className="text-sm font-medium">Payment</span>
                </div>
              </div>

              {step === "review" && (
                <Card className="border-border">
                  <CardHeader>
                    <CardTitle className="text-foreground">Review Your Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Player Info */}
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground mb-3">Your Account</h3>
                      <div className="bg-secondary/50 rounded-lg p-4">
                        <p className="font-medium text-foreground">{user.displayName}</p>
                        <p className="text-sm text-muted-foreground">{user.email}</p>
                      </div>
                    </div>

                    {/* Optional: Role Selection for team-based games */}
                    {(league.game === "League of Legends" || league.game === "Overwatch 2") && (
                      <div>
                        <label className="text-sm font-medium text-foreground mb-2 block">
                          Preferred Role (Optional)
                        </label>
                        <Input
                          placeholder="e.g., Support, DPS, Top Lane"
                          value={preferredRole}
                          onChange={(e) => setPreferredRole(e.target.value)}
                          className="bg-background/50 border-border"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          We&apos;ll try to accommodate your preference when forming teams.
                        </p>
                      </div>
                    )}

                    {/* Rules Agreement */}
                    <div className="bg-secondary/30 rounded-lg p-4 border border-border">
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          id="rules"
                          checked={agreedToRules}
                          onChange={(e) => setAgreedToRules(e.target.checked)}
                          className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-primary"
                        />
                        <label htmlFor="rules" className="text-sm text-muted-foreground">
                          I agree to the{" "}
                          <Link href="/rules" className="text-primary hover:underline">league rules</Link>
                          {" "}and commit to showing up for scheduled match nights. I understand that 
                          repeated no-shows may result in removal from the league.
                        </label>
                      </div>
                    </div>

                    <Button
                      onClick={() => setStep("payment")}
                      disabled={!agreedToRules}
                      className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                    >
                      Continue to Payment
                    </Button>
                  </CardContent>
                </Card>
              )}

              {step === "payment" && (
                <Card className="border-border">
                  <CardHeader>
                    <CardTitle className="text-foreground flex items-center gap-2">
                      <CreditCard className="h-5 w-5" />
                      Payment
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <label className="text-sm font-medium text-foreground mb-2 block">
                        Card Number
                      </label>
                      <Input
                        placeholder="4242 4242 4242 4242"
                        className="bg-background/50 border-border"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-foreground mb-2 block">
                          Expiration
                        </label>
                        <Input
                          placeholder="MM/YY"
                          className="bg-background/50 border-border"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-foreground mb-2 block">
                          CVC
                        </label>
                        <Input
                          placeholder="123"
                          className="bg-background/50 border-border"
                        />
                      </div>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-muted-foreground bg-secondary/50 rounded-lg p-3">
                      <Shield className="h-4 w-4 text-primary" />
                      <span>Payments secured by Stripe</span>
                    </div>

                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        onClick={() => setStep("review")}
                        className="flex-1 border-border"
                      >
                        Back
                      </Button>
                      <Button
                        onClick={handlePayment}
                        disabled={isLoading}
                        className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground"
                      >
                        {isLoading ? "Processing..." : `Pay $${league.entryFee}`}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Order Summary Sidebar */}
            <div className="lg:col-span-2">
              <Card className="border-border sticky top-24">
                <CardHeader>
                  <CardTitle className="text-foreground text-lg">Order Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="font-semibold text-foreground">{league.name}</p>
                    <p className="text-sm text-muted-foreground">{league.game}</p>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Calendar className="h-4 w-4 text-primary" />
                      <span>{league.day}s @ {league.time} {league.timezone}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="h-4 w-4 text-primary" />
                      <span>{league.region}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Clock className="h-4 w-4 text-primary" />
                      <span>{league.weeksDuration} week season</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Users className="h-4 w-4 text-primary" />
                      <span>{league.spotsLeft} spots remaining</span>
                    </div>
                  </div>

                  <div className="border-t border-border pt-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Season fee</span>
                      <span className="text-foreground">${league.entryFee}.00</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Processing fee</span>
                      <span className="text-foreground">$0.00</span>
                    </div>
                    <div className="flex justify-between font-semibold pt-2 border-t border-border">
                      <span className="text-foreground">Total</span>
                      <span className="text-primary">${league.entryFee}.00</span>
                    </div>
                  </div>

                  <div className="bg-secondary/50 rounded-lg p-3 text-xs text-muted-foreground">
                    <p className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-accent" />
                      <span>
                        Refunds available up to 48 hours before the season starts. 
                        No refunds after the first match.
                      </span>
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}
